import React, { useState, useEffect } from 'react';
import { toValidObjectIdString } from '../utils/security';
import { formatMesafeBaslik } from '../utils/mesafeFormat';
import IntegerInput from './IntegerInput';
import { parseAdet } from '../utils/integerInput';

const formatCurrency = (amount) => {
  if (amount == null) return '—';
  return `${parseFloat(amount).toFixed(2)} ₺`;
};

const roundMoney = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;

const buildYolSatir = (mesafeRaw, yolBedeliRaw) => {
  const mesafe = Math.max(0, parseFloat(mesafeRaw) || 0);
  const toplam = Math.max(0, parseFloat(yolBedeliRaw) || 0);
  const birimFiyat = mesafe > 0 ? roundMoney(toplam / mesafe) : 0;
  return { mesafe, birimFiyat, toplam };
};

const RandevuBedeliOnayModal = ({ bedel, onClose, onSubmit, loading, fetchDetay }) => {
  const [satirlar, setSatirlar] = useState([]);
  const [yolSatir, setYolSatir] = useState({ mesafe: 0, birimFiyat: 0, toplam: 0 });
  const [mesafeGidisDonus, setMesafeGidisDonus] = useState(false);
  const [detayLoading, setDetayLoading] = useState(true);

  const applyDetay = (rb, fallbackBedel) => {
    const rows = Array.isArray(rb?.satirlar) ? rb.satirlar : (fallbackBedel?.satirlar || []);
    setSatirlar(rows.filter((s) => s.tip !== 'yol' && s.tip !== 'yolBedeli'));
    setYolSatir(buildYolSatir(
      rb?.mesafe ?? fallbackBedel?.mesafe ?? 0,
      rb?.yolBedeli ?? fallbackBedel?.yolBedeli ?? 0,
    ));
    setMesafeGidisDonus((rb?.mesafeGidisDonus ?? fallbackBedel?.mesafeGidisDonus) === true);
  };

  useEffect(() => {
    const id = toValidObjectIdString(bedel?.id ?? bedel?._id);
    if (!id || !fetchDetay) {
      applyDetay(bedel, bedel);
      setDetayLoading(false);
      return;
    }
    setDetayLoading(true);
    fetchDetay(id)
      .then((res) => {
        if (res?.basarili && res?.veri?.randevuBedeli) {
          applyDetay(res.veri.randevuBedeli, bedel);
        } else {
          applyDetay(bedel, bedel);
        }
      })
      .catch(() => applyDetay(bedel, bedel))
      .finally(() => setDetayLoading(false));
  }, [bedel?.id, bedel?._id, bedel?.satirlar, bedel?.yolBedeli, bedel?.mesafe, fetchDetay]);

  const handleSatirChange = (idx, field, value) => {
    setSatirlar((prev) => {
      const next = [...prev];
      if (!next[idx]) return next;
      next[idx] = { ...next[idx], [field]: field === 'fiyat' || field === 'adet' ? (parseFloat(value) || 0) : value };
      if (field === 'adet') next[idx].adet = Math.max(1, parseInt(value, 10) || 1);
      if (field === 'fiyat') next[idx].fiyat = Math.max(0, parseFloat(value) || 0);
      return next;
    });
  };

  const handleYolBirimFiyatChange = (value) => {
    const birimFiyat = Math.max(0, parseFloat(value) || 0);
    setYolSatir((prev) => ({
      ...prev,
      birimFiyat,
      toplam: roundMoney(birimFiyat * prev.mesafe),
    }));
  };

  const handleYolMesafeChange = (value) => {
    const mesafe = Math.max(0, parseFloat(value) || 0);
    setYolSatir((prev) => ({
      ...prev,
      mesafe,
      toplam: roundMoney(prev.birimFiyat * mesafe),
    }));
  };

  const handleYolToplamChange = (value) => {
    const toplam = Math.max(0, parseFloat(value) || 0);
    setYolSatir((prev) => ({
      mesafe: prev.mesafe,
      toplam,
      birimFiyat: prev.mesafe > 0 ? roundMoney(toplam / prev.mesafe) : 0,
    }));
  };

  const addIlaveSatir = () => {
    setSatirlar((prev) => [...prev, { tip: 'ilave', islemAdi: 'Ekstra ücret', parcaAdi: null, fiyat: 0, adet: 1, aciklama: null }]);
  };

  const removeSatir = (idx) => {
    setSatirlar((prev) => prev.filter((_, i) => i !== idx));
  };

  const islemToplam = satirlar.reduce(
    (s, row) => s + (parseFloat(row.fiyat) || 0) * (parseInt(row.adet, 10) || 1),
    0,
  );
  const toplam = islemToplam + (parseFloat(yolSatir.toplam) || 0);

  const handleSubmit = () => {
    onSubmit({
      satirlar: satirlar.map((s) => ({
        tip: s.tip || 'ilave',
        islemAdi: s.islemAdi || '',
        parcaAdi: s.parcaAdi || '',
        fiyat: parseFloat(s.fiyat) || 0,
        adet: Math.max(1, parseInt(s.adet, 10) || 1),
        aciklama: s.aciklama || '',
      })),
      yolBedeli: roundMoney(yolSatir.toplam),
      mesafe: roundMoney(yolSatir.mesafe),
    });
  };

  const getTipLabel = (tip) => {
    if (tip === 'islem') return 'İşlem';
    if (tip === 'parca') return 'Parça';
    if (tip === 'yol' || tip === 'yolBedeli') return 'Yol bedeli';
    return 'İlave';
  };

  const inputBase = {
    width: '100%',
    padding: '8px 10px',
    fontSize: '14px',
    color: '#1e293b',
    backgroundColor: '#fff',
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    boxSizing: 'border-box',
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content randevu-bedeli-onay-modal" style={{ maxWidth: '920px', width: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Randevu Bedeli Onayı — {bedel?.randevuNumarasi || '?'}</h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto', overflowX: 'auto', flex: '1', minHeight: 0 }}>
          {detayLoading ? (
            <div style={{ padding: '24px', textAlign: 'center' }}>Yükleniyor...</div>
          ) : (
            <>
              <p style={{ marginBottom: '16px', fontSize: '14px', color: 'var(--text-gray)' }}>
                Fiyat ve adetleri düzenleyebilir, ekstra satır ekleyebilirsiniz. Yol bedelinde km × birim fiyat = toplam;
                toplamı değiştirdiğinizde birim fiyat otomatik güncellenir (iskonto).
              </p>
              <div style={{ overflowX: 'auto', marginBottom: '16px' }}>
                <table className="admins-table" style={{ minWidth: '720px', width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '88px' }}>Tip</th>
                      <th style={{ minWidth: '180px' }}>Açıklama / Kod</th>
                      <th style={{ width: '110px', textAlign: 'right' }}>Fiyat (₺)</th>
                      <th style={{ width: '100px', minWidth: '100px', textAlign: 'center' }}>Adet</th>
                      <th style={{ width: '120px', textAlign: 'right' }}>Toplam (₺)</th>
                      <th style={{ width: '48px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {satirlar.map((row, idx) => {
                      const satirToplam = (parseFloat(row.fiyat) || 0) * (parseInt(row.adet, 10) || 1);
                      return (
                        <tr key={`satir-${idx}`}>
                          <td style={{ verticalAlign: 'middle' }}>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-gray)' }}>
                              {getTipLabel(row.tip)}
                            </span>
                          </td>
                          <td style={{ verticalAlign: 'middle' }}>
                            {(row.tip === 'islem' || row.tip === 'parca') ? (
                              <>
                                <span style={{ fontSize: '14px', color: '#1e293b' }}>{row.islemAdi || row.parcaAdi || '—'}</span>
                                {(row.islemKodu || row.parcaKodu) && (
                                  <div style={{ fontSize: '12px', color: 'var(--text-gray)', marginTop: '4px' }}>
                                    Kod: {row.islemKodu || row.parcaKodu}
                                  </div>
                                )}
                              </>
                            ) : (
                              <input
                                type="text"
                                value={row.islemAdi || row.parcaAdi || ''}
                                onChange={(e) => handleSatirChange(idx, 'islemAdi', e.target.value)}
                                style={{ ...inputBase }}
                                placeholder="Açıklama"
                              />
                            )}
                          </td>
                          <td style={{ verticalAlign: 'middle' }}>
                            <input
                              type="number"
                              min={0}
                              step={0.01}
                              value={row.fiyat ?? ''}
                              onChange={(e) => handleSatirChange(idx, 'fiyat', e.target.value)}
                              style={{ ...inputBase, textAlign: 'right', minWidth: '80px' }}
                            />
                          </td>
                          <td style={{ verticalAlign: 'middle', width: '100px', minWidth: '100px' }}>
                            <IntegerInput
                              min={1}
                              value={row.adet ?? 1}
                              onChange={(v) =>
                                handleSatirChange(idx, 'adet', parseAdet(v, { min: 1 }))
                              }
                              style={{ ...inputBase, textAlign: 'center', width: '100%', minWidth: '88px' }}
                            />
                          </td>
                          <td style={{ textAlign: 'right', verticalAlign: 'middle', fontSize: '14px', fontWeight: 500 }}>
                            {formatCurrency(satirToplam)}
                          </td>
                          <td style={{ verticalAlign: 'middle' }}>
                            <button type="button" className="category-action-btn delete-btn" onClick={() => removeSatir(idx)} title="Sil">
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    <tr key="yol-bedeli" style={{ background: 'rgba(241, 245, 249, 0.6)' }}>
                      <td style={{ verticalAlign: 'middle' }}>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#0f766e' }}>
                          Yol bedeli
                        </span>
                      </td>
                      <td style={{ verticalAlign: 'middle' }}>
                        <span style={{ fontSize: '14px', color: '#1e293b' }}>{formatMesafeBaslik(mesafeGidisDonus)} × km birim fiyat</span>
                        <div style={{ fontSize: '12px', color: 'var(--text-gray)', marginTop: '4px' }}>
                          Adet = km · Fiyat = ₺/km
                        </div>
                      </td>
                      <td style={{ verticalAlign: 'middle' }}>
                        <input
                          type="number"
                          min={0}
                          step={0.0001}
                          value={yolSatir.birimFiyat ?? ''}
                          onChange={(e) => handleYolBirimFiyatChange(e.target.value)}
                          style={{ ...inputBase, textAlign: 'right', minWidth: '80px' }}
                          title="Km birim fiyat (₺/km)"
                        />
                      </td>
                      <td style={{ verticalAlign: 'middle', width: '100px', minWidth: '100px' }}>
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          value={yolSatir.mesafe ?? ''}
                          onChange={(e) => handleYolMesafeChange(e.target.value)}
                          style={{ ...inputBase, textAlign: 'center', width: '100%', minWidth: '88px' }}
                          title={mesafeGidisDonus ? 'Mesafe (Gidiş-Dönüş, km)' : 'Mesafe (km)'}
                        />
                      </td>
                      <td style={{ verticalAlign: 'middle' }}>
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          value={yolSatir.toplam ?? ''}
                          onChange={(e) => handleYolToplamChange(e.target.value)}
                          style={{ ...inputBase, textAlign: 'right', minWidth: '88px', fontWeight: 600 }}
                          title="Yol bedeli toplam — değiştirince birim fiyat güncellenir"
                        />
                      </td>
                      <td style={{ verticalAlign: 'middle' }} />
                    </tr>
                  </tbody>
                </table>
              </div>
              <button type="button" className="secondary-btn" onClick={addIlaveSatir} style={{ marginBottom: '16px' }}>
                <i className="fas fa-plus"></i> Ekstra satır ekle
              </button>
              <div style={{ padding: '14px 16px', background: 'var(--bg-light, #f1f5f9)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <strong style={{ fontSize: '15px' }}>Genel toplam: {formatCurrency(toplam)}</strong>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px 20px', borderTop: '1px solid var(--border-color)' }}>
          <button type="button" className="secondary-btn" onClick={onClose}>
            İptal
          </button>
          <button type="button" className="primary-btn" onClick={handleSubmit} disabled={detayLoading || loading}>
            {loading ? 'Kaydediliyor...' : 'Onayla'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RandevuBedeliOnayModal;
